/** Integer minor units. €1.00 is 100. Quantities use milli-units: 1 guest is 1000. */

const SAFE = BigInt(Number.MAX_SAFE_INTEGER);

export function divRoundHalfAway(numerator: bigint, denominator: bigint): number {
  if (denominator === 0n) throw new Error("division by zero");
  const negative = (numerator < 0n) !== (denominator < 0n);
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const quotient = n / d;
  const remainder = n % d;
  const rounded = remainder * 2n >= d ? quotient + 1n : quotient;
  const signed = negative ? -rounded : rounded;
  if (signed > SAFE || signed < -SAFE) throw new Error("amount exceeds safe integer range");
  return Number(signed);
}

export function lineAmount(quantityMilli: number, unitMinor: number): number {
  return divRoundHalfAway(BigInt(quantityMilli) * BigInt(unitMinor), 1000n);
}

export function percentOf(amountMinor: number, bps: number): number {
  return divRoundHalfAway(BigInt(amountMinor) * BigInt(bps), 10000n);
}

export function formatMoney(minor: number | null | undefined, currency = "EUR", locale = "en-GB"): string {
  if (minor == null || !Number.isFinite(minor)) return "—";
  const negative = minor < 0;
  const abs = Math.abs(Math.trunc(minor));
  const major = Math.trunc(abs / 100);
  const cents = abs % 100;
  const decimal = Number(`${major}.${cents.toString().padStart(2, "0")}`);
  const formatted = new Intl.NumberFormat(locale, { style: "currency", currency }).format(decimal);
  return negative ? `-${formatted.replace(/^-/, "")}` : formatted;
}

/** Largest-remainder allocation. Shares are non-negative and sum to `total`. */
export function allocateLargestRemainder(weights: number[], total: number): number[] {
  if (total <= 0 || weights.length === 0) return weights.map(() => 0);
  const weightSum = weights.reduce((sum, value) => sum + value, 0);
  if (weightSum <= 0) return weights.map(() => 0);
  const shares = weights.map((weight) => Math.floor((weight * total) / weightSum));
  let remaining = total - shares.reduce((sum, value) => sum + value, 0);
  const order = weights
    .map((weight, index) => ({ index, remainder: (weight * total) % weightSum }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const item of order) {
    if (remaining <= 0) break;
    shares[item.index] += 1;
    remaining -= 1;
  }
  return shares;
}
